import React from 'react';
import { Sidebar, Menu, Icon, List, Accordion, AccordionPanelProps, SemanticICONS } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import { SubMenu, MainMenu, SubMenux as SubMenuX} from '../constants';
import Logo from '../styles/images/logoMain.png';
import { Account } from "../model/user";
import { useActions } from '../redux/actions';
import * as appActions from '../redux/actions/app';
import { menusArray } from '../menus';
import { SIcon, IconPropsName } from './icons/SIcon';
import * as partnersActions from "../redux/actions/partners";

interface Props {
  visible: boolean,
  user: Account,
  savePartnerUsage: Function
}

const hasRequiredRole = (menuRoles: string[] | undefined, userRole: string[]): boolean => {
  if (!menuRoles || !menuRoles.length) {
    return true;
  }
  const lowerCaseUserRole = userRole.map(r => r.toLowerCase());
  return menuRoles.some(role => lowerCaseUserRole.includes(role.toLowerCase()));
};

const parsePath = (path: string) => `/${path.toLowerCase().replace(/ /g, '-')}`;

export default function SideBarMain(props: Props) {
  const partnersAction = useActions(partnersActions);
  const appAction = useActions(appActions);
  const { user, visible, savePartnerUsage } = props;

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    partnersAction.getDentalPartners().then((res: { partners_uri?: { mycdp_uri?: string } }) => {
      if (res.partners_uri?.mycdp_uri) {
        window.location.href = res.partners_uri.mycdp_uri;
      }
    });
  };

  const closeSideBar = () => appAction.SidebarOpen(false);

  const [basePath, childPath] = window.location.pathname.substring(1).split('/').map(p => p.replace(/-/g, ' '));
  const basePathIndex = menusArray.findIndex(i => i.name.toLowerCase() === basePath?.toLowerCase());

  const renderSubSubMenuItem = (subSubMenu: SubMenuX, parentLink: string, j: number) => {
    if (!hasRequiredRole(subSubMenu.role, user.role)) {
      return null;
    }
    const subSubChildLink = `${parentLink}${parsePath(subSubMenu.name)}`;
    const isActive = childPath === subSubMenu.name.toLowerCase();

    return (
      <List.Item key={`sub-panel-${j}-${subSubMenu.pageId}`} as='div' className={isActive ? 'active' : ''}>
        <Link style={{ color: 'black' }} to={subSubChildLink} onClick={closeSideBar} className={isActive ? 'active' : ''}>
          {subSubMenu.customIcon
            ? <i aria-hidden="true" className={`${subSubMenu.customIcon} icon`} />
            : <SIcon name={subSubMenu.icon as IconPropsName} style={{ margin: "-6px 12px -6px 0" }} />
          }
          <span>{subSubMenu.title}</span>
        </Link>
      </List.Item>
    );
  };

  const renderChildMenuItem = (subMenu: SubMenu, i: number, parentLink: string): AccordionPanelProps | JSX.Element | null => {
    if (subMenu.hide || !hasRequiredRole(subMenu.role, user.role)) {
      return null;
    }

    if (subMenu.subMenux && subMenu.subMenux.length > 0) {
      const subSubMenus = subMenu.subMenux.map((subSubMenu, k) => renderSubSubMenuItem(subSubMenu, parentLink, k)).filter(Boolean);
      if (subSubMenus.length === 0) return null;

      return {
        key: `panel-${i}-${subMenu.pageId}`,
        title: {
          content: (
            <span className={basePath === subMenu.name ? 'parentActive' : ''}>
              {subMenu.customIcon ? <i aria-hidden="true" className={`${subMenu.customIcon} icon`} /> : <Icon name={subMenu.icon as SemanticICONS} />}
              <span>{subMenu.title}</span>
            </span>
          )
        },
        content: { content: <List>{subSubMenus}</List>, className: 'p0' }
      };
    }

    const childLink = `${parentLink}${parsePath(subMenu.name)}`;
    const isActive = childPath === subMenu.name.toLowerCase();

    return (
      <List key={`listj-${i}`}>
        <List.Item as='div' className={isActive ? 'active' : ''}>
          <Link style={{ color: 'black' }} to={childLink} onClick={closeSideBar} className={isActive ? 'active' : ''}>
            {subMenu.customIcon ? <i aria-hidden="true" className={`${subMenu.customIcon} icon`} /> : <Icon name={subMenu.icon as SemanticICONS} />}
            <span>{subMenu.title}</span>
          </Link>
        </List.Item>
      </List>
    );
  };

  const renderMainMenu = (menu: MainMenu, i: number): AccordionPanelProps => {
    const key = `panel-${i}-${menu.name}`;

    if (menu.hide || !hasRequiredRole(menu.role, user.role)) {
      return { key };
    }

    const parentLink = parsePath(menu.name);

    if (menu.subMenus && menu.subMenus.length > 0) {
      const childMenus = menu.subMenus.map((subMenu, j) => renderChildMenuItem(subMenu, j, parentLink)).filter(Boolean) as AccordionPanelProps[];
      if (childMenus.length === 0) return { key };
      return {
        key,
        title: {
          content: (
            <span className={(basePath === menu.name.toLowerCase()) ? 'parentActive' : ''}>
              {menu.customIcon
                ? <i aria-hidden="true" className={`${menu.customIcon} icon`} />
                : <SIcon name={menu.icon as IconPropsName} style={{ margin: "-6px 12px -6px 0" }} />}
              <span>{menu.title}</span>
              <Icon name='angle down' className="float-right" />
            </span>
          )
        },
        content: { content: <Accordion.Accordion className="mt0 rounded0" panels={childMenus} />, key: 'sub-accordions', className: 'p0' }
      };
    }

    return {
      key,
      title: {
        content: <Link
          onClick={() => {
            if (menu.partner) savePartnerUsage(menu.partner);
            closeSideBar();
          }}
          to={parentLink}
        >
          {menu.customIcon
            ? <i aria-hidden="true" className={`${menu.customIcon} icon`} />
            : <SIcon name={menu.icon as IconPropsName} style={{ margin: "-5px 12px -5px 0" }} />}
          <span>{menu.title}</span>
        </Link>
      }
    };
  };

  const panels: AccordionPanelProps[] = menusArray.map(renderMainMenu);

  return (
    <Sidebar
      as={Menu}
      animation='push'
      width='thin'
      visible={visible}
      icon='labeled'
      vertical
      inverted
    >
      <div className="logoHolder">
        <a onClick={handleClick}>
          <img src={Logo} className="logo align-self-center" alt="Logo" />
        </a>
      </div>
      <Accordion className="cdp-sidebar styled leftMenu" panels={panels} defaultActiveIndex={basePathIndex} />
    </Sidebar>
  );
}
